import React, { useMemo } from 'react';

import type { CurrencyInfo } from '@src/domains/orders';
import { getCouponDiscountCurrencyInfo, SaleCouponTypeEnum, type SaleCoupon } from '@src/domains/coupons';
import type { DetailedSaleItem } from '@src/domains/sale-items';
import useTranslate from '@src/i18n/useTranslate';
import useCurrencyFormat from '@src/hooks/useCurrencyFormat';
import PackageItemGroup from '../../Package/SalePackageItemGroup';
import SaleItem from './SaleItem';
import { PurchaseResume, ResumeValue, SaleItemList, Total } from './styles';
import { groupSaleItemsByProductPackage } from '@src/domains/cart';

interface SaleSummaryContentProps {
  items: DetailedSaleItem[];
  subtotal: CurrencyInfo;
  coupon: SaleCoupon | null | undefined;
  total: CurrencyInfo;
  showTotal: boolean;
  showPlaceholderImage: boolean;
}

export const SaleSummaryContent = (props: SaleSummaryContentProps) => {
  const { items, subtotal, coupon, total, showTotal, showPlaceholderImage } = props;

  const { i18n } = useTranslate();
  const { formatCurrencyInfo } = useCurrencyFormat();

  const groupedSaleItems = useMemo(() => groupSaleItemsByProductPackage(items), [items]);

  return (
    <>
      <SaleItemList>
        {groupedSaleItems.packages.map((packageItem, index) => (
          <PackageItemGroup
            key={`${packageItem.productPackage.name}-${index}`}
            productPackage={packageItem.productPackage}
            items={packageItem.items}
            showPlaceholderImage={showPlaceholderImage}
          />
        ))}

        {groupedSaleItems.nonPackageItems?.map((item) => (
          <SaleItem key={item.id} item={item} showPlaceholderImage={showPlaceholderImage} />
        ))}
      </SaleItemList>

      <PurchaseResume>
        <ResumeValue>
          <p>{i18n('direct_link_summary_subtotal')}</p>
          <p>{formatCurrencyInfo(subtotal)}</p>
        </ResumeValue>

        {coupon ? (
          <ResumeValue>
            <p>{i18n('direct_link_summary_coupon')}</p>
            <p>{coupon.code}</p>
          </ResumeValue>
        ) : null}

        {coupon ? (
          <ResumeValue>
            <p>{i18n('direct_link_summary_discount')}</p>
            <p>
              {coupon.type === SaleCouponTypeEnum.Percentage ? <span>{`(-${coupon.formattedDiscount})`}</span> : null}

              {formatCurrencyInfo(getCouponDiscountCurrencyInfo(coupon))}
            </p>
          </ResumeValue>
        ) : null}

        {showTotal ? (
          <Total>
            <p>{i18n('direct_link_summary_total')}</p> <p>{formatCurrencyInfo(total)}</p>
          </Total>
        ) : null}
      </PurchaseResume>
    </>
  );
};

export default SaleSummaryContent;
